/**
 * A classificação do CPTu: SBT (Robertson 2010, não normalizado), SBTn (Robertson 1990), SBTn
 * modificado (Robertson 2016) e Schneider et al. (2008) — e o nome e o token da cor de cada zona
 * (spec 07 §5.2, item 3, e §6.3; RN-252; os valores das cores moram em `design/testesolo.tokens.css`).
 *
 * As zonas são `Int8Array`, uma posição por leitura; 0 = sem classe (a conta não existe ou o
 * ponto cai fora de toda zona). Entram as séries do básico (`basico.ts`): qt em MPa (qt/pa com
 * pa = 101,325 kPa), Rf, Fr, Qtn, Qt1, U₂ e I(B), todas calculadas sobre a média móvel de 3
 * leituras, como no CPeT-IT.
 *
 * O ponto de partida do plano era: zonas 2 a 7 pelo Ic (fronteiras 1,31 · 2,05 · 2,60 · 2,95 ·
 * 3,60), zona 1 abaixo de 12·exp(−1,4·Fr) e zonas 8 e 9 acima da curva de Robertson. O gabarito
 * 2609350 (dez verticais, 18.711 leituras) decidiu o resto; cada escolha está abaixo com a
 * evidência — a fração das 18.711 linhas em que a zona do motor é a do gabarito, com a escolha e
 * com a alternativa, e a pior vertical (o critério do §5.3 é ≥ 99 % em cada uma).
 *
 * As fontes estão no repositório:
 *  · `test/amostras-de-arquivo/cptu-2609350/abacos/robertson-1990.json` — o ábaco de Robertson
 *    que o CPeT-IT desenha atrás dos gráficos SBT (pág. 2) e SBTn (pág. 3) do PDF do relatório
 *    2609350 com dissipações (a MESMA imagem de 369 × 444 px nas duas páginas), digitalizado sem
 *    nada do gabarito por `ferramentas/cptu-abacos/robertson.py`;
 *  · `ferramentas/cptu-abacos/refinar.mjs` — o ajuste das fronteiras ao gabarito (escolha 1), que
 *    reproduz `FRONTEIRAS_ROBERTSON` e diz se a tabela do motor é a dele;
 *  · `test/amostras-de-arquivo/cptu-2609350/abacos/schneider-2008.json` — o traçado vetorial do
 *    ábaco de Schneider da pág. 4 (`ferramentas/cptu-abacos/schneider.py`);
 *  · `test/amostras-de-arquivo/cptu-2609350/abacos/divergencias-zonas.csv` — TODAS as leituras em
 *    que a zona do motor não é a do gabarito (`ferramentas/cptu-abacos/divergencias.mjs`); o
 *    `298.30` cobra a lista e o `298.35` cobra o desenho.
 *  O LEIA-ME de `ferramentas/cptu-abacos/` diz como rodar cada um.
 *
 * SBT e SBTn — o ábaco desenhado, uma tabela só
 *
 *  1. O CPeT-IT classifica pelas fronteiras DESENHADAS do ábaco de Robertson (1990, fig. 1), o
 *     mesmo desenho no SBT, e não pelos círculos do Ic que as aproximam: no gabarito a zona 3 do
 *     SBTn tem Ic de 2,64 a 3,68 e a zona 4 de 2,36 a 2,99, e a zona 0 aparece onde o ponto cai em
 *     cima de uma linha do desenho (a base, o lado da caixa CCS, as curvas do ábaco de 2016). Pelo
 *     Ic, as zonas 2 a 7 batem em 93,23 % das linhas no SBTn (pior vertical: CPTu06, 89,45 %) e
 *     em 94,66 % no SBT (CPTu04, 90,63 %). As fronteiras desenhadas não têm equação publicada; o
 *     motor usa o desenho digitalizado: oito polilinhas em log10 × log10 (x = Fr ou Rf, %; y =
 *     Qtn ou qt/pa) — o topo da zona 1, a borda da zona 2 (o pé 1|2 junto da base e a 2|3), 3|4,
 *     4|5, 5|6, 6|7, a borda de baixo das zonas 8 e 9 e a divisa 8|9 —, com os vértices do
 *     desenho (a cada 0,1 década e nas pontas, onde a linha encontra outra fronteira ou a borda).
 *     O desenho puro dá 99,51 % no SBTn (CPTu07, 98,85 %) e 99,17 % no SBT (CPTu07, 98,22 %) —
 *     abaixo dos 99 % por vertical que o `298.30` cobra: a imagem tem 1 px = 1,25 % em Fr e 1,56 %
 *     em Qtn, grossa para as leituras rente às linhas. Por isso o v de cada vértice foi ajustado
 *     ao gabarito numa faixa de ±0,02 década em volta do desenho (≈ 3 px), só onde há leituras ao
 *     alcance do vértice (`refinar.mjs`: o vértice vai para o v que deixa menos leituras do lado
 *     errado, SBT e SBTn juntos; empate e falta de leitura deixam o desenho, exato) — AJUSTE DO
 *     MOTOR AO GABARITO, SEM FONTE PUBLICADA — A ASSINAR PELO RT [cla.fronteiras-ajustadas]. Saíram
 *     do desenho 35 dos 93 vértices, no máximo 0,013 década, todos dentro da nuvem de leituras do
 *     gabarito. O desenho é o do PDF do relatório (CPeT-IT 3.9.7.6); a exportação do gabarito é do
 *     CPeT-IT 3.9.7.4. Além das pontas vale a reta com a inclinação do último trecho DESENHADO.
 *     Uma tabela só para os dois ábacos: SBTn 99,968 % (6 linhas fora; pior: CPTu05, 99,93 %) e
 *     SBT 99,834 % (31; pior: CPTu07, 99,42 %).
 *  2. O topo da zona 1 desenhado fica entre 12,0 e 12,5·exp(−1,4·Fr) de Fr 0,13 a 1,26 (a curva
 *     de partida do plano, 12·exp(−1,4·Fr), corre um pouco abaixo); o pé encontra a base em
 *     Fr 1,69 — no SBT o gabarito passa a zona 1|2 da base entre Rf 1,66 e 1,71.
 *  3. A borda de baixo das zonas 8 e 9 desenhada segue a curva publicada de Robertson,
 *     1/(0,005·(Fr − 1) − 0,0003·(Fr − 1)² − 0,002), a no máximo 0,021 década de Fr 4 a 10,
 *     onde está quase toda a zona 9 do gabarito; de Fr 1,4 a 4 (só seis leituras, da zona 8,
 *     Qtn 105 a 156) o desenho passa até 0,07 década acima dela e chega ao topo em Fr 1,43, não
 *     em 1,62. A divisa 8|9 não tem equação: é a curva desenhada (Fr 3,99 em Qtn 113, 4,29 em
 *     158, 4,70 em 316, 4,93 em 1.000); ela acerta as 14 leituras de 8 e 9 do gabarito com
 *     Fr < 4,5 (uma reta em Fr = 4 erraria uma, CPTu03 a 0,18 m; em Fr = 4,5, oito).
 *  4. Fora do ábaco — o que o CPeT-IT faz ali não tem fonte publicada; as regras e a evidência de
 *     cada uma, todas do gabarito:
 *     · à esquerda (x < 0,1 %) — COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A ASSINAR
 *       PELO RT [cla.esquerda]: sem classe, menos abaixo da base (y < 1), onde é zona 1. No SBT
 *       são 34 leituras de superfície (Rf ≈ 0, qt/pa < 1) com zona 1 no gabarito e 101 com zona
 *       0 acima da base; no SBTn o gabarito não tem leitura nenhuma à esquerda abaixo da base (a
 *       regra vale pelo SBT, sem evidência própria);
 *     · abaixo da base (y < 1) — COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A ASSINAR
 *       PELO RT [cla.abaixo-da-base]: a zona do ponto na base (y = 1);
 *     · à direita do SBTn (Fr > 10 %) — COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A
 *       ASSINAR PELO RT [cla.direita-sbtn]: a zona do ponto em Fr = 10 (o gabarito tem zona 2 até
 *       Qtn 7,84 e zona 3 a partir de 8,11, onde a 2|3 corta a borda);
 *     · à direita do SBT (Rf > 10 %) — COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A
 *       ASSINAR PELO RT [cla.direita-sbt]: zona 2 abaixo de qt/pa 1, zona 3 até qt/pa 5,63, zona 9
 *       acima — aqui o CPeT-IT NÃO segue as fronteiras (dentro do ábaco, junto à borda, o
 *       gabarito tem zona 2 até qt/pa 6,7); o 5,63 fica entre a zona 3 a qt/pa 5,50 e a 9 a 5,76
 *       (as duas no CPTu06). A zona 9 além da borda se apoia em 9 leituras de 2 verticais (CPTu06
 *       de 0,27 a 0,30 m e de 17,92 a 17,95 m; CPTu07 a 18,05 m); e na borda a zona SALTA para 9
 *       sem passar pelas do ábaco — de 3 acima de qt/pa 7,86, de 2 entre 5,63 e 7,86: no CPTu07,
 *       18,04 m é zona 3 (Rf 9,66), 18,05 m zona 9 (Rf 10,60, qt/pa 10,07) e 18,06 m zona 3 de
 *       novo (Rf 9,96). Com a regra do SBTn (a zona do ponto em Rf = 10) a coluna cairia para
 *       99,32 % (CPTu06, 98,52 %). A borda é a do ábaco, Rf = 10: o gabarito ainda tem zona 2 em
 *       Rf 10,004 (CPTu03 a 0,24 m), que fica na lista de divergências.
 *     Ficam divergências de costura do CPeT-IT (zona 0 em qt/pa ≈ 1,00–1,01 no SBT, em cima da
 *     base desenhada) e leituras rente a uma linha; estão todas em `divergencias-zonas.csv`.
 *  4b. Acima do topo (y > 1000) — ESCOLHA DO MOTOR POR ANALOGIA, SEM EVIDÊNCIA DO GABARITO NEM
 *     FONTE PUBLICADA — A ASSINAR PELO RT [cla.acima-do-topo] (tarefa 311.5): a zona do ponto no
 *     topo (y = 1000), como à direita vale a da borda direita (Fr ou Rf = 10). Sem isso, a reta da
 *     borda de baixo das zonas 8 e 9, prolongada além do desenho, passaria a zona 7 para 8 (em
 *     Fr 1, a partir de Qtn ≈ 2.280). O gabarito não tem leitura acima do topo (o maior Qtn é 454
 *     e o maior qt/pa, 478 — o qt da média móvel, que é o que o classificador recebe): não há
 *     como saber o que o CPeT-IT faz ali, e as contagens de divergência não mudam.
 *
 * SBTn modificado — Robertson (2016)
 *
 *  5. I(B) = 100·(Qtn + 10)/(70 + Qtn·Fr) (do básico) separa argiloso (I(B) < 22), transicional
 *     (22 ≤ I(B) ≤ 32) e arenoso (I(B) > 32); CD = (Qtn − 11)·(1 + 0,06·Fr)¹⁷ ≥ 70 é dilatante,
 *     abaixo é contrátil. Os limites são os publicados, e são as curvas I(B) = 22, I(B) = 32 e
 *     CD = 70 do desenho de 2016 (pág. 9 do PDF); a linha desenhada passa um pouco ao lado da
 *     conta (zona 0 e troca de zona com I(B) entre 22,0 e 22,3 e entre 32,0 e 32,3; com CD entre
 *     70 e 72 o CPeT-IT ainda diz contrátil em 32 linhas, e com CD entre 68 e 70 já diz
 *     dilatante em 5). CD ≥ 71 acertaria 16 linhas a mais, mas sem fonte: fica o 70.
 *  6. CCS (argiloso — contrátil — sensível) é a caixa Fr < 2 % e Qtn < 10 — os dois lados retos
 *     do desenho de 2016 —, e vale antes do I(B): ela cobre também o canto transicional (I(B)
 *     22–26) abaixo de Qtn 10. Evidência: CCS até Fr 1,99965 e Qtn 9,9995; CC a partir de
 *     Fr 2,0028 e TC a partir de Qtn 10,0009; zona 0 em 13 linhas com Fr de 2,0001 a 2,0059 e
 *     Qtn < 10 (em cima do lado desenhado; o motor não imita). Só no argiloso (sem cobrir o
 *     transicional) a coluna cai para 94,09 % (CPTu07, 84,19 %); pela sensibilidade St = Ns/Fr
 *     > 3 no argiloso contrátil, para 85,81 % (CPTu05, 74,63 %).
 *  7. Fora do ábaco — COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A ASSINAR PELO RT
 *     [cla.mod-fora-do-abaco]: à esquerda (Fr < 0,1 %) sem classe; à direita (Fr > 10 %), CCS abaixo de Qtn 10 e CC acima
 *     — o CPeT-IT não classifica ali pelo I(B) nem pelo CD (sem a regra, 98,47 %, CPTu02
 *     97,36 %). A borda é a do ábaco, Fr = 10; o gabarito ainda classifica pelo ábaco até
 *     Fr 10,03 (5 leituras na lista de divergências). Com tudo: 99,492 % (95 linhas fora; pior:
 *     CPTu06, 99,19 %).
 *
 * Schneider et al. (2008) — sem gabarito
 *
 *  8. O ábaco Q × U₂ (Q = Qt1 = (qt − σv)/σ'v; U₂ = Δu₂/σ'v), zonas 1a (siltes e argilas de
 *     baixo Ir), 1b (argilas), 1c (argilas sensíveis), 2 (areias essencialmente drenadas) e 3
 *     (solos de transição), nos códigos 1 a 5. O gabarito não tem coluna de Schneider e o artigo
 *     (J. Geotech. Geoenviron. Eng. 134(11):1569–1586) não foi achado em fonte aberta (nem as
 *     fronteiras, em manual de programa ou artigo que as reproduza); as fronteiras são o traçado
 *     do CPeT-IT (pág. 4 do PDF, «Bq plots (Schneider)», linhas vetoriais), em fórmulas ajustadas
 *     a ele — `FONTE_SCHNEIDER` diz isso às telas. O desvio de cada fórmula ao traçado, em
 *     log10 U₂ (rms e máximo; o traçado tem ±0,004 década de ruído de pixel), está em
 *     `schneider-2008.json`:
 *     · zona 2: |U₂| < 1 e Q > 20·e^(2·|U₂|) — o «funil» com paredes em U₂ = ±1 (0,007 e 0,016
 *       década em log10 Q);
 *     · 3 | 1a: U₂ = 1 + Q^1,25/100 (0,003 e 0,009);
 *     · 1a | 1b: U₂ = 1,05 + Q^0,95/5 (0,003 e 0,007);
 *     · 1b | 1c: U₂ = 1,2 + Q^0,914/1,536 (0,004 e 0,011);
 *     · à direita da reta Bq = 1 (U₂ ≥ Q), 1c: o desenho termina as curvas 1a|1b e 1b|1c nessa
 *       reta (em Q ≈ 1,3 e Q ≈ 3) e não tem outra zona do lado de lá; acima de Q ≈ 3 a 1c já
 *       passa dela.
 *     Fica fora do PDF e do Portal (RN-252; a marca `sem_gabarito` vem na tarefa 311.7) até
 *     alguém conferir essas fronteiras contra o artigo.
 *
 * O desenho dos ábacos (onda 313) — a tela desenha só as linhas que classificam: a moldura
 * (`MOLDURA_DO_ABACO`), as fronteiras de Robertson pela polilinha (`valorNaPolilinha` sobre
 * `FRONTEIRAS_ROBERTSON`), os limites e as curvas do SBTn modificado (`LIMITES_SBTN_MOD`, `cdDe`,
 * `CURVAS_SBTN_MOD`) e as curvas de Schneider (`CURVAS_SCHNEIDER`, com `FONTE_SCHNEIDER` escrito junto).
 * As funções de zona usam esses mesmos limites e as mesmas expressões, e nenhuma zona mudou com a
 * exportação (o `300.04` prende a grade de cada ábaco por hash). Os círculos do Ic não se desenham: não
 * são o que classifica (escolha 1).
 *
 * As premissas não entram na classificação nesta versão: o ΔIc do `.cpt` (0 no 2609350) não tem
 * fronteira de Ic onde atuar, porque as zonas do CPeT-IT são as dos ábacos (escolha 1). As do básico
 * (média móvel, γ, u₀, corte de Cn) mudam as zonas através do básico — a conformidade lista quais
 * (`condicoes`, `conformidade.ts`).
 */
import type { Basico } from './basico';
import type { Premissas, RegraAAssinar } from './tipos';
import { PA_KPA } from './unidades';

/* ── Os nomes e as cores ─────────────────────────────────────────────────────────────────── */

/** As zonas do SBT e do SBTn (as mesmas nove nos dois ábacos: Robertson 2010, tabela 1). */
export type ZonaSbtn = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

/** As zonas do SBTn modificado (Robertson 2016), nos códigos do gabarito («Mod. SBTn»). */
export type ZonaSbtnMod = 1 | 2 | 3 | 4 | 5 | 6 | 7;

/**
 * Uma zona SBT/SBTn: o token da cor, nome, nome curto e a descrição que o CPeT-IT exporta. O motor
 * não guarda cor literal (P-UI-01: toda cor vem de `design/testesolo.tokens.css`): `token` é o nome
 * da propriedade CSS (`--ts-sbtn-1` … `--ts-sbtn-9`). A tela pinta com `var(${token})`; o PDF (onda
 * 315) resolve o hex no arquivo de tokens; e a zona vai sempre escrita (número e nome) junto da cor.
 */
export interface DescricaoZonaSbtn {
  token: string;
  nome: string;
  nomeCurto: string;
  ingles: string;
}

/**
 * Uma zona do SBTn modificado: nome, sigla, o token da cor (`--ts-sbtnmod-1` … `--ts-sbtnmod-7`, como
 * em `DescricaoZonaSbtn`: a tela usa `var(${token})`, o PDF resolve o hex no arquivo de tokens, e a
 * zona vai escrita junto da cor) e a descrição do CPeT-IT.
 */
export interface DescricaoZonaSbtnMod {
  nome: string;
  sigla: 'CCS' | 'CC' | 'CD' | 'TC' | 'TD' | 'SC' | 'SD';
  token: string;
  ingles: string;
}

/** Uma zona de Schneider: o rótulo do ábaco (1a, 1b, 1c, 2, 3) e o nome. */
export interface DescricaoZonaSchneider {
  codigo: string;
  nome: string;
}

/** Congela a tabela e cada linha dela: ninguém muda um token ou um nome por engano. */
function congelar<T extends object>(tabela: T): Readonly<T> {
  for (const linha of Object.values(tabela)) Object.freeze(linha);
  return Object.freeze(tabela);
}

const zonasSbtn: Record<ZonaSbtn, DescricaoZonaSbtn> = {
  1: { token: '--ts-sbtn-1', nome: 'solo fino sensível', nomeCurto: 'fino sensível', ingles: 'Sensitive fine grained' },
  2: { token: '--ts-sbtn-2', nome: 'solo orgânico', nomeCurto: 'orgânico', ingles: 'Organic soil' },
  3: { token: '--ts-sbtn-3', nome: 'argila a argila siltosa', nomeCurto: 'argila', ingles: 'Clay' },
  4: {
    token: '--ts-sbtn-4', nome: 'mistura de silte (silte argiloso a argila siltosa)', nomeCurto: 'silte',
    ingles: 'Clay & silty clay',
  },
  5: {
    token: '--ts-sbtn-5', nome: 'mistura de areia (areia siltosa a silte arenoso)', nomeCurto: 'areia siltosa',
    ingles: 'Silty sand & sandy silt',
  },
  6: { token: '--ts-sbtn-6', nome: 'areia limpa a areia siltosa', nomeCurto: 'areia', ingles: 'Sand & silty sand' },
  7: { token: '--ts-sbtn-7', nome: 'areia densa a pedregulhosa', nomeCurto: 'areia densa', ingles: 'Sand' },
  8: {
    token: '--ts-sbtn-8', nome: 'areia a areia argilosa muito rija', nomeCurto: 'areia rija',
    ingles: 'Very stiff sand/clayey sand',
  },
  9: { token: '--ts-sbtn-9', nome: 'solo fino muito rijo', nomeCurto: 'fino rijo', ingles: 'Very stiff fine grained' },
};

/**
 * Nome, nome curto e token da cor de cada zona SBT/SBTn (spec §6.3: as cores do CPeT-IT, medidas na
 * folha do relatório, decisão D10; os valores moram em `design/testesolo.tokens.css`). `ingles` é a
 * descrição que o CPeT-IT exporta («SBTn Description»). A cor nunca vai sozinha: a zona vai escrita,
 * número e nome, junto dela.
 */
export const ZONAS_SBTN: Readonly<Record<ZonaSbtn, Readonly<DescricaoZonaSbtn>>> = congelar(zonasSbtn);

const zonasSbtnMod: Record<ZonaSbtnMod, DescricaoZonaSbtnMod> = {
  1: {
    nome: 'argiloso — contrátil — sensível', sigla: 'CCS', token: '--ts-sbtnmod-1',
    ingles: 'Clay-like - Contractive - Sensitive',
  },
  2: { nome: 'argiloso — contrátil', sigla: 'CC', token: '--ts-sbtnmod-2', ingles: 'Clay-like - Contractive' },
  3: { nome: 'argiloso — dilatante', sigla: 'CD', token: '--ts-sbtnmod-3', ingles: 'Clay-like - Dilative' },
  4: { nome: 'transicional — contrátil', sigla: 'TC', token: '--ts-sbtnmod-4', ingles: 'Transitional - Contractive' },
  5: { nome: 'transicional — dilatante', sigla: 'TD', token: '--ts-sbtnmod-5', ingles: 'Transitional - Dilative' },
  6: { nome: 'arenoso — contrátil', sigla: 'SC', token: '--ts-sbtnmod-6', ingles: 'Sand-like - Contractive' },
  7: { nome: 'arenoso — dilatante', sigla: 'SD', token: '--ts-sbtnmod-7', ingles: 'Sand like - Dilative' },
};

/**
 * Nome, sigla e token da cor de cada zona do SBTn modificado. `ingles` é a descrição do gabarito
 * («SBTn Mod. Description», com o «Sand like» sem hífen como o CPeT-IT escreve); as cores, em
 * `design/testesolo.tokens.css`, são as da legenda «Mod. SBTn» do CPeT-IT (folha 8 do relatório
 * 2609350).
 */
export const ZONAS_SBTN_MOD: Readonly<Record<ZonaSbtnMod, Readonly<DescricaoZonaSbtnMod>>> = congelar(zonasSbtnMod);

const zonasSchneider: Record<number, DescricaoZonaSchneider> = {
  1: { codigo: '1a', nome: 'siltes e argilas de baixo Ir' },
  2: { codigo: '1b', nome: 'argilas' },
  3: { codigo: '1c', nome: 'argilas sensíveis' },
  4: { codigo: '2', nome: 'areias essencialmente drenadas' },
  5: { codigo: '3', nome: 'solos de transição' },
};

/** As zonas de Schneider et al. (2008), nos códigos 1 a 5 (`codigo` é o rótulo do ábaco). */
export const ZONAS_SCHNEIDER: Readonly<Record<number, Readonly<DescricaoZonaSchneider>>> = congelar(zonasSchneider);

/**
 * De onde vêm as fronteiras de Schneider (escolha 8): as telas (313/316) escrevem isto junto das
 * regiões do ábaco, que ficam marcadas como não conferidas; a coluna é `sem_gabarito`.
 */
export const FONTE_SCHNEIDER = 'traçado do CPeT-IT, não conferido com o artigo';

/* ── O ábaco de Robertson (SBT e SBTn) ───────────────────────────────────────────────────── */

/**
 * Uma fronteira do ábaco em log10 × log10: os vértices (t = coordenada independente, v =
 * dependente), em ordem crescente de t, ligados por retas. As pontas são onde a linha desenhada
 * encontra outra fronteira ou a borda do ábaco; além delas vale a reta com a inclinação do
 * último trecho DESENHADO (`antes`, `depois`: dv/dt), não a do trecho ajustado (escolha 1).
 */
export interface Polilinha {
  readonly t: readonly number[];
  readonly v: readonly number[];
  readonly antes: number;
  readonly depois: number;
}

/**
 * O valor da polilinha em t (log10): a interpolação entre os vértices e, além das pontas, a reta com a
 * inclinação do último trecho desenhado. É a conta das funções de zona, exportada para a tela desenhar
 * as fronteiras do ábaco com os mesmos números que classificam (onda 313).
 */
export function valorNaPolilinha(f: Polilinha, t: number): number {
  const n = f.t.length;
  if (t <= f.t[0]) return f.v[0] + (t - f.t[0]) * f.antes;
  if (t >= f.t[n - 1]) return f.v[n - 1] + (t - f.t[n - 1]) * f.depois;
  let i = 0;
  while (t > f.t[i + 1]) i++;
  return f.v[i] + ((t - f.t[i]) * (f.v[i + 1] - f.v[i])) / (f.t[i + 1] - f.t[i]);
}

/**
 * As fronteiras do ábaco de Robertson (1990), uma tabela só para o SBT e o SBTn: o CPeT-IT
 * desenha os dois gráficos sobre a mesma imagem (escolha 1). x = log10(Fr ou Rf, %), y =
 * log10(Qtn ou qt/pa). `f23` e `divisa89` dão x em função de y; as demais, y em função de x.
 */
export interface FronteirasRobertson {
  /** O topo da zona 1 (abaixo dele: zona 1). */
  readonly f1: Polilinha;
  /** A borda da zona 2 (à direita dela: zona 2): o pé 1|2 junto da base, depois a 2|3. */
  readonly f23: Polilinha;
  /** 3|4, 4|5, 5|6 e 6|7 (abaixo: a zona de número menor). */
  readonly f34: Polilinha;
  readonly f45: Polilinha;
  readonly f56: Polilinha;
  readonly f67: Polilinha;
  /** A borda de baixo das zonas 8 e 9 (acima dela: 8 ou 9). */
  readonly f89: Polilinha;
  /** A divisa 8|9 (à esquerda: 8; à direita: 9). */
  readonly divisa89: Polilinha;
}

function congelarFronteiras(f: FronteirasRobertson): Readonly<FronteirasRobertson> {
  for (const p of Object.values(f) as Polilinha[]) {
    Object.freeze(p.t);
    Object.freeze(p.v);
    Object.freeze(p);
  }
  return Object.freeze(f);
}

/**
 * As fronteiras do motor: os vértices do desenho digitalizado
 * (`test/amostras-de-arquivo/cptu-2609350/abacos/robertson-1990.json`, mesmos t), com o v
 * ajustado ao gabarito em no máximo 0,02 década onde ele tem leituras (`refinar.mjs`) — AJUSTE
 * DO MOTOR AO GABARITO, SEM FONTE PUBLICADA — A ASSINAR PELO RT [cla.fronteiras-ajustadas]
 * (escolha 1); onde não tem, o v do desenho, exato. O `298.35` confere vértice a vértice.
 */
export const FRONTEIRAS_ROBERTSON: Readonly<FronteirasRobertson> = congelarFronteiras({
  f1: {
    t: [-0.9917, -0.9, -0.8, -0.7, -0.6, -0.5, -0.4, -0.3, -0.2, -0.1, 0, 0.1, 0.2137],
    v: [1.0079, 1.0071, 0.9961, 0.9742, 0.9475, 0.9073, 0.8528, 0.7936, 0.7129, 0.6143, 0.4856, 0.3279, 0.0632],
    antes: -0.0087, depois: -2.2577,
  },
  f23: {
    t: [0.0035, 0.0372, 0.0507, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.8595],
    v: [0.2283, 0.2264, 0.242, 0.3457, 0.5067, 0.6184, 0.7117, 0.7905, 0.8557, 0.9087, 0.9606, 0.9837],
    antes: -0.0564, depois: 0.4555,
  },
  f34: {
    t: [-0.1214, 0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.765],
    v: [0.6802, 0.7581, 0.8202, 0.8937, 0.9799, 1.0878, 1.2123, 1.3712, 1.5553, 1.7193],
    antes: 0.6087, depois: 2.4923,
  },
  f45: {
    t: [-0.4836, -0.4, -0.3, -0.2, -0.1, 0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.5704],
    v: [0.9354, 0.9876, 1.0299, 1.0783, 1.1385, 1.2096, 1.2957, 1.3794, 1.484, 1.6009, 1.7516, 1.934],
    antes: 0.4689, depois: 2.4631,
  },
  f56: {
    t: [-0.9917, -0.9, -0.8, -0.7, -0.6, -0.5, -0.4, -0.3, -0.2, -0.1, 0, 0.1, 0.2, 0.3, 0.3921],
    v: [
      1.4274, 1.4332, 1.4426, 1.4577, 1.482, 1.5144, 1.5525, 1.6083,
      1.6679, 1.7283, 1.8153, 1.9096, 2.0297, 2.1773, 2.3365,
    ],
    antes: 0.0632, depois: 1.7286,
  },
  f67: {
    t: [-0.9917, -0.9, -0.8, -0.7, -0.5, -0.4, -0.3, -0.2, -0.1, -0.0079],
    v: [2.1936, 2.2103, 2.2361, 2.2668, 2.3645, 2.4228, 2.5001, 2.6126, 2.7645, 2.9448],
    antes: 0.1821, depois: 1.9577,
  },
  f89: {
    t: [0.1542, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 0.9975],
    v: [2.9614, 2.8435, 2.6053, 2.3831, 2.1768, 1.983, 1.8576, 1.7929, 1.7543, 1.7474],
    antes: -2.5742, depois: -0.1221,
  },
  divisa89: {
    t: [2.0526, 2.1, 2.2, 2.3, 2.4, 2.5, 2.6, 2.7, 2.8, 2.9, 2.9895],
    v: [0.6008, 0.6121, 0.6324, 0.6485, 0.6617, 0.6718, 0.6785, 0.6843, 0.6896, 0.6932, 0.6933],
    antes: 0.2384, depois: 0.0011,
  },
});

/**
 * A moldura dos ábacos de Robertson (SBT, SBTn e SBTn modificado): a borda esquerda (Fr ou Rf, %), a
 * borda direita, a base e o topo (Qtn ou qt/pa) — os eixos log × log que a tela desenha (onda 313) e as
 * bordas que as funções de zona usam (as regras de fora do ábaco, escolhas 4, 4b e 7).
 */
export const MOLDURA_DO_ABACO: Readonly<{ xMinimo: 0.1; xMaximo: 10; base: 1; topo: 1000 }> = Object.freeze({
  xMinimo: 0.1, xMaximo: 10, base: 1, topo: 1000,
} as const);

const { xMinimo: X_MINIMO, base: BASE, xMaximo: X_MAXIMO, topo: TOPO } = MOLDURA_DO_ABACO;

/** SBT além da borda direita: zona 3 até qt/pa 5,63, zona 9 acima (escolha 4, copiado do CPeT-IT). */
const SBT_ALEM_DA_BORDA_3_9 = 5.63;

/** A zona de um ponto dentro do ábaco (x entre 0,1 e 10; y entre 1 e 1000). */
function zonaNoAbaco(f: FronteirasRobertson, x: number, y: number): number {
  const lx = Math.log10(x);
  const ly = Math.log10(y);
  if (ly > valorNaPolilinha(f.f89, lx)) return lx < valorNaPolilinha(f.divisa89, ly) ? 8 : 9;
  if (lx > valorNaPolilinha(f.f23, ly)) return 2;
  if (ly < valorNaPolilinha(f.f1, lx)) return 1;
  if (ly <= valorNaPolilinha(f.f34, lx)) return 3;
  if (ly <= valorNaPolilinha(f.f45, lx)) return 4;
  if (ly <= valorNaPolilinha(f.f56, lx)) return 5;
  if (ly <= valorNaPolilinha(f.f67, lx)) return 6;
  return 7;
}

/** O que os dois ábacos fazem antes de olhar as fronteiras: sem número e à esquerda (escolha 4). */
function antesDoAbaco(x: number, y: number): number | null {
  if (!Number.isFinite(x) || !Number.isFinite(y) || !(y > 0) || x < 0) return 0;
  if (x < X_MINIMO) return y < BASE ? 1 : 0;
  return null;
}

/**
 * DIAGNÓSTICO — NÃO USAR NO FLUXO OFICIAL: a zona SBTn de um ponto pelas fronteiras DADAS (o
 * `refinar.mjs` passa as do desenho puro e as que vai ajustando); quem classifica é `zonaSbtn`, com
 * `FRONTEIRAS_ROBERTSON`. 0 = sem classe.
 */
export function zonaSbtnCom(f: FronteirasRobertson, fr: number, qtn: number): number {
  const fora = antesDoAbaco(fr, qtn);
  if (fora !== null) return fora;
  return zonaNoAbaco(f, Math.min(fr, X_MAXIMO), Math.min(Math.max(qtn, BASE), TOPO));
}

/**
 * DIAGNÓSTICO — NÃO USAR NO FLUXO OFICIAL: a zona SBT de um ponto pelas fronteiras DADAS; quem
 * classifica é `zonaSbt`, com `FRONTEIRAS_ROBERTSON`. 0 = sem classe.
 */
export function zonaSbtCom(f: FronteirasRobertson, rf: number, qtPa: number): number {
  const fora = antesDoAbaco(rf, qtPa);
  if (fora !== null) return fora;
  if (rf > X_MAXIMO) return qtPa < BASE ? 2 : qtPa < SBT_ALEM_DA_BORDA_3_9 ? 3 : 9;
  return zonaNoAbaco(f, rf, Math.min(Math.max(qtPa, BASE), TOPO));
}

/** A zona SBTn (Robertson 1990) de um ponto: Fr em %, Qtn adimensional. 0 = sem classe. */
export function zonaSbtn(fr: number, qtn: number): number {
  return zonaSbtnCom(FRONTEIRAS_ROBERTSON, fr, qtn);
}

/** A zona SBT (Robertson 2010, não normalizado) de um ponto: Rf em %, qt/pa. 0 = sem classe. */
export function zonaSbt(rf: number, qtPa: number): number {
  return zonaSbtCom(FRONTEIRAS_ROBERTSON, rf, qtPa);
}

/* ── O SBTn modificado (Robertson 2016) ──────────────────────────────────────────────────── */

/**
 * Os limites do SBTn modificado (escolhas 5 e 6): I(B) argiloso abaixo de 22, arenoso acima de 32; CD
 * dilatante a partir de 70; a caixa CCS, Fr < 2 % e Qtn < 10. Exportados para a tela desenhar o ábaco de
 * 2016 com os números que classificam (onda 313).
 */
export const LIMITES_SBTN_MOD: Readonly<{ ibArgiloso: 22; ibArenoso: 32; cdDilatante: 70; ccsFr: 2; ccsQtn: 10 }> =
  Object.freeze({ ibArgiloso: 22, ibArenoso: 32, cdDilatante: 70, ccsFr: 2, ccsQtn: 10 } as const);

const {
  ibArgiloso: IB_ARGILOSO, ibArenoso: IB_ARENOSO, cdDilatante: CD_DILATANTE, ccsFr: CCS_FR, ccsQtn: CCS_QTN,
} = LIMITES_SBTN_MOD;

/** CD = (Qtn − 11)·(1 + 0,06·Fr)¹⁷ (Robertson 2016; escolha 5): a conta que o `zonaSbtnMod` compara com 70. */
export function cdDe(fr: number, qtn: number): number {
  return (qtn - 11) * (1 + 0.06 * fr) ** 17;
}

/** A curva de uma fronteira de ábaco (y em função de x); NaN onde ela não existe — nunca Infinity. */
export type CurvaDoAbaco = (x: number) => number;

/** Qtn = a/(100 − b·Fr): a curva I(B) = constante isolada em Qtn; NaN onde o denominador não é positivo. */
const curvaDoIb = (a: number, b: number): CurvaDoAbaco => (fr) => {
  const denominador = 100 - b * fr;
  return Number.isFinite(denominador) && denominador > 0 ? a / denominador : NaN;
};

/**
 * Qtn = 11 + 70/(1 + 0,06·Fr)¹⁷, a curva CD = 70; NaN com 1 + 0,06·Fr ≤ 0 (Fr ≤ −16,67) e onde a potência 17
 * vai a zero e a divisão daria Infinity.
 */
const curvaDoCd70: CurvaDoAbaco = (fr) => {
  const base = 1 + 0.06 * fr;
  if (!(Number.isFinite(base) && base > 0)) return NaN;
  const qtn = 11 + 70 / base ** 17;
  return Number.isFinite(qtn) ? qtn : NaN;
};

/**
 * As curvas do ábaco de 2016 em Qtn por Fr, SÓ PARA DESENHAR — quem classifica é o `zonaSbtnMod`, que
 * compara o I(B) e o CD (`cdDe`) com os limites, e não estas curvas (no ponto flutuante a comparação pela
 * curva poderia trocar uma zona rente à linha). Saem da definição I(B) = 100·(Qtn + 10)/(70 + Qtn·Fr) e
 * do CD:
 *  · I(B) = 22: Qtn = 540/(100 − 22·Fr), com Fr < 4,545 (100/22; dali em diante, NaN);
 *  · I(B) = 32: Qtn = 1240/(100 − 32·Fr), com Fr < 3,125 (100/32; dali em diante, NaN);
 *  · CD = 70: Qtn = 11 + 70/(1 + 0,06·Fr)¹⁷, com 1 + 0,06·Fr > 0 (fora, NaN).
 */
export const CURVAS_SBTN_MOD: Readonly<{ ib22: CurvaDoAbaco; ib32: CurvaDoAbaco; cd70: CurvaDoAbaco }> = Object.freeze({
  ib22: curvaDoIb(540, 22),
  ib32: curvaDoIb(1240, 32),
  cd70: curvaDoCd70,
});

/**
 * A zona do SBTn modificado de um ponto (escolhas 5 a 7): Fr em %, Qtn e I(B) do básico.
 * 0 = sem classe.
 */
export function zonaSbtnMod(fr: number, qtn: number, ib: number): number {
  if (!Number.isFinite(fr) || !Number.isFinite(qtn) || !Number.isFinite(ib)) return 0;
  if (!(fr >= X_MINIMO) || !(qtn > 0)) return 0;
  if (fr > X_MAXIMO) return qtn < CCS_QTN ? 1 : 2; // além da borda direita: copiado do CPeT-IT
  if (fr < CCS_FR && qtn < CCS_QTN) return 1;
  const dilatante = cdDe(fr, qtn) >= CD_DILATANTE;
  if (ib > IB_ARENOSO) return dilatante ? 7 : 6;
  if (ib >= IB_ARGILOSO) return dilatante ? 5 : 4;
  return dilatante ? 3 : 2;
}

/* ── Schneider et al. (2008) ─────────────────────────────────────────────────────────────── */

/**
 * As fronteiras de Schneider (escolha 8; `FONTE_SCHNEIDER`), as mesmas expressões que o `zonaSchneider`
 * compara — exportadas para a tela desenhar o ábaco Q × U₂ (onda 313):
 *  · `3|1a`, `1a|1b` e `1b|1c`: U₂ em função de Q (= Qt1);
 *  · `bq1`: a reta Bq = 1, U₂ = Q (à direita dela, 1c);
 *  · `zona2QMinimo`: o Q acima do qual é zona 2, em função de U₂: 20·e^(2·|U₂|), com |U₂| < 1 (fora, NaN).
 */
export const CURVAS_SCHNEIDER: Readonly<{
  '3|1a': CurvaDoAbaco;
  '1a|1b': CurvaDoAbaco;
  '1b|1c': CurvaDoAbaco;
  bq1: CurvaDoAbaco;
  zona2QMinimo: CurvaDoAbaco;
}> = Object.freeze({
  '3|1a': (q: number) => 1 + q ** 1.25 / 100,
  '1a|1b': (q: number) => 1.05 + q ** 0.95 / 5,
  '1b|1c': (q: number) => 1.2 + q ** 0.914 / 1.536,
  bq1: (q: number) => q,
  zona2QMinimo: (u2: number) => (Math.abs(u2) < 1 ? 20 * Math.exp(2 * Math.abs(u2)) : NaN),
});

/** A zona de Schneider de um ponto (escolha 8): Q = Qt1 e U₂ = Δu₂/σ'v. 0 = sem classe. */
export function zonaSchneider(qt1: number, u2: number): number {
  if (!Number.isFinite(qt1) || !Number.isFinite(u2) || !(qt1 > 0)) return 0;
  if (qt1 > CURVAS_SCHNEIDER.zona2QMinimo(u2)) return 4; // 2: areias drenadas (com |U₂| ≥ 1 o mínimo é NaN: nunca)
  if (u2 <= CURVAS_SCHNEIDER['3|1a'](qt1)) return 5; // 3: transição
  if (u2 >= CURVAS_SCHNEIDER.bq1(qt1)) return 3; // 1c: à direita de Bq = 1, onde as curvas 1a|1b e 1b|1c terminam
  if (u2 <= CURVAS_SCHNEIDER['1a|1b'](qt1)) return 1; // 1a
  if (u2 <= CURVAS_SCHNEIDER['1b|1c'](qt1)) return 2; // 1b
  return 3; // 1c
}

/* ── As regras que o RT assina ───────────────────────────────────────────────────────────── */

/**
 * Os comportamentos da classificação sem fonte publicada (escolhas 1, 4, 4b e 7 do cabeçalho), um
 * por rótulo do comentário, com o mesmo id entre colchetes: a conformidade (`conformidade.ts`) os
 * leva ao RT para assinar, e o `298.64` confere que a lista e os rótulos são os mesmos.
 */
export const REGRAS_A_ASSINAR_CLASSIFICACAO: readonly RegraAAssinar[] = Object.freeze(
  [
    {
      id: 'cla.fronteiras-ajustadas',
      arquivo: 'classificacao.ts',
      descricao: 'As fronteiras SBT e SBTn são as do ábaco de Robertson (1990) que o CPeT-IT desenha, digitalizado '
        + 'do PDF do relatório 2609350 (CPeT-IT 3.9.7.6; a exportação do gabarito é do 3.9.7.4), com 35 dos 93 '
        + 'vértices movidos até 0,013 década, todos dentro da nuvem de leituras do gabarito, para o lado que erra '
        + 'menos leituras (refinar.mjs). Uma tabela só para os dois ábacos.',
      evidencia: 'Com o ajuste, SBTn 99,968 % das 18.711 linhas (pior vertical: CPTu05, 99,93 %) e SBT 99,834 % '
        + '(CPTu07, 99,42 %); com o desenho puro, SBTn 99,51 % (CPTu07, 98,85 %) e SBT 99,17 % (CPTu07, 98,22 %), '
        + 'abaixo dos 99 % por vertical — a imagem tem 1 px = 1,25 % em Fr e 1,56 % em Qtn. Pelos círculos do Ic, '
        + '93,23 % (SBTn) e 94,66 % (SBT).',
    },
    {
      id: 'cla.esquerda',
      arquivo: 'classificacao.ts',
      descricao: 'SBT e SBTn à esquerda do ábaco (Rf ou Fr < 0,1 %): sem classe, menos abaixo da base '
        + '(qt/pa ou Qtn < 1), onde é zona 1.',
      evidencia: 'No SBT, 34 leituras de superfície (Rf ≈ 0, qt/pa < 1) com zona 1 no gabarito e 101 com zona 0 '
        + 'acima da base; no SBTn o gabarito não tem leitura à esquerda abaixo da base (a regra vale pelo SBT).',
    },
    {
      id: 'cla.abaixo-da-base',
      arquivo: 'classificacao.ts',
      descricao: 'SBT e SBTn abaixo da base do ábaco (qt/pa ou Qtn < 1): a zona do ponto na base.',
      evidencia: 'CPTu08 a 0,01 m (qt/pa 0,03): zona 1 no SBT, a da base; CPTu02 a 0,95 m (Fr 137 %, Qtn 0,40): '
        + 'zona 2 no SBTn (298.34).',
    },
    {
      id: 'cla.direita-sbtn',
      arquivo: 'classificacao.ts',
      descricao: 'SBTn à direita do ábaco (Fr > 10 %): a zona do ponto em Fr = 10.',
      evidencia: 'O gabarito tem zona 2 até Qtn 7,84 e zona 3 a partir de 8,11, onde a fronteira 2|3 corta a borda '
        + 'Fr = 10.',
    },
    {
      id: 'cla.direita-sbt',
      arquivo: 'classificacao.ts',
      descricao: 'SBT à direita do ábaco (Rf > 10 %): zona 2 abaixo de qt/pa 1, zona 3 até qt/pa 5,63 e zona 9 '
        + 'acima — ali o CPeT-IT não segue as fronteiras desenhadas.',
      evidencia: 'A zona 9 além da borda se apoia em 9 leituras de 2 verticais (CPTu06 de 0,27 a 0,30 m e de 17,92 a '
        + '17,95 m; CPTu07 a 18,05 m); o corte 5,63 fica entre a zona 3 a qt/pa 5,50 e a zona 9 a 5,76 (as duas no '
        + 'CPTu06). Na borda Rf = 10 a zona salta para 9 sem passar pelas do ábaco: no CPTu07, 3 a 18,04 m (Rf '
        + '9,66), 9 a 18,05 m (Rf 10,60, qt/pa 10,07) e 3 a 18,06 m (Rf 9,96); dentro do ábaco, junto à borda, o '
        + 'gabarito tem zona 2 até qt/pa 6,7. Com a regra do SBTn (a zona do ponto em Rf = 10), 99,32 % (CPTu06, '
        + '98,52 %).',
    },
    {
      id: 'cla.acima-do-topo',
      arquivo: 'classificacao.ts',
      descricao: 'SBT e SBTn acima do topo do ábaco (qt/pa ou Qtn > 1000): a zona do ponto no topo — escolha do '
        + 'motor por analogia com a borda direita, sem evidência do gabarito nem fonte publicada.',
      evidencia: 'O gabarito não tem leitura acima do topo (o maior Qtn é 454 e o maior qt/pa, 478); sem a regra, a '
        + 'borda de baixo das zonas 8 e 9, prolongada, passaria a zona 7 para 8 (em Fr 1, a partir de Qtn ≈ 2.280).',
    },
    {
      id: 'cla.mod-fora-do-abaco',
      arquivo: 'classificacao.ts',
      descricao: 'SBTn modificado (2016) fora do ábaco: à esquerda (Fr < 0,1 %) sem classe; à direita (Fr > 10 %), '
        + 'CCS abaixo de Qtn 10 e CC acima, sem o I(B) nem o CD.',
      evidencia: 'Com a regra, 99,492 % das 18.711 linhas (pior vertical: CPTu06, 99,19 %); sem ela, 98,47 % '
        + '(CPTu02, 97,36 %).',
    },
  ].map((r) => Object.freeze(r)),
);

/* ── A classificação de uma vertical ─────────────────────────────────────────────────────── */

/**
 * As quatro classificações de uma vertical, leitura a leitura, a partir do básico. Devolve
 * séries novas (a entrada não muda) e nunca lança: o que não tem classe é 0. As premissas não
 * mudam nada nesta versão (ver o cabeçalho); ficam no contrato.
 */
export function classificar(
  b: Basico,
  _premissas: Premissas,
): { sbt: Int8Array; sbtn: Int8Array; sbtnMod: Int8Array; schneider: Int8Array } {
  const n = b.total;
  const sbt = new Int8Array(n);
  const sbtn = new Int8Array(n);
  const sbtnMod = new Int8Array(n);
  const schneider = new Int8Array(n);
  for (let i = 0; i < n; i++) {
    sbt[i] = zonaSbt(b.rf[i], (1000 * b.qt[i]) / PA_KPA);
    sbtn[i] = zonaSbtn(b.fr[i], b.qtn[i]);
    sbtnMod[i] = zonaSbtnMod(b.fr[i], b.qtn[i], b.ib[i]);
    schneider[i] = zonaSchneider(b.qt1[i], b.U2[i]);
  }
  return { sbt, sbtn, sbtnMod, schneider };
}
